import {ServiceBase} from "../ServiceBase";
import {ISessionTimerService} from "./session-timer.service.interface";
import {TimeSpan} from "@sparkware/app-utils";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {observable, runInAction, makeObservable} from "mobx";
import {namedSetInterval} from "../../../common/utils/time-functions";
import {IRealityCheckHandler} from "./reality-check/handlers/reality-check-handler.interface";
import {Lazy} from "../../../utils/lazy";
import {SESSION_TIMER_HEARTBEAT_INTERVAL_HIGH_RATE, SESSION_TIMER_HEARTBEAT_INTERVAL_LOW_RATE, SESSION_TIMER_INCREMENT_INTERVAL} from "./session-timer-constants";



export class SessionTimerService extends ServiceBase implements ISessionTimerService {
    constructor(services: IServiceFactoryExtended) {
        super(services);

        makeObservable(this);

        this.reaction(() => this.services.user.isAuthorized,
            async (isAuthorized) => {
                if (isAuthorized) {
                    await this._startSessionTimer();
                } else {
                    this._stopSessionTimer();
                    //we dispose it here only on logout because while the user is logged in we don't want to destroy reality check handler local storage data.
                    //This reaction can execute because the user just open the app (after 2 days for example) in which case we need to clear the state from 2 days ago,
                    //but also this reaction can be executed because the user refresh the page. If he is not logged in then is fine to destroy the handler,
                    //but if he is logged in we don't want to destroy the state of the handler.
                    this._disposeRealityCheckHandler();
                }
        }, {
            fireImmediately: true
        });

        this.reaction(() => this.services.application.isActive,
            async (isActive) => {
                if (isActive && this.isEnabled) {
                    clearTimeout(this._appActivateDebounceTimerRef);
                    //This is for the case when the user switches back and forth between tabs/apps
                    this._appActivateDebounceTimerRef = setTimeout(async () => {
                        try {
                            this.currentSessionTimer = await this._readSessionTimerFromServer();
                        } catch (err) {
                            this.services.logger.error('Failed to update session timer from server on app activate', err);
                        }

                    }, 1000);
                } else {
                    clearTimeout(this._appActivateDebounceTimerRef);
                }
            });
    }

    private _incrementIntervalRef: any = null;
    private _heartBeatIntervalRef: any = null;
    private _appActivateDebounceTimerRef: any = null;

    get isEnabled(): boolean {
        return this.services.user.isAuthorized && this.services.regulation.current.isSessionTimerEnabled;
    }

    @observable.ref
    private _currentSessionTimer: TimeSpan = TimeSpan.fromMilliseconds(0);
    get currentSessionTimer(): TimeSpan {
        return this._currentSessionTimer;
    }

    private set currentSessionTimer(value: TimeSpan | null) {
        runInAction(() => {
            this._currentSessionTimer = value;
            if(value) {
                this.services.sdk.eventsTrigger.sessionTimerUpdate(value);
                this.realityCheckHandler.handleRealityCheck(value);
            }
        });


    }

    private _realityCheckHandler: Lazy<IRealityCheckHandler> = new Lazy<IRealityCheckHandler>(() => {
        return this.services.regulation.current.createRealityCheckHandler();
    });
    private get realityCheckHandler(): IRealityCheckHandler {
        return this._realityCheckHandler.value;
    }

    private _disposeRealityCheckHandler(): void {
        this.realityCheckHandler.dispose();
        this._realityCheckHandler = new Lazy<IRealityCheckHandler>(() => {
            return this.services.regulation.current.createRealityCheckHandler();
        })
    }



    private async _startSessionTimer(): Promise<void> {
        if(!this.isEnabled) {
            return;
        }

        try {
            this.currentSessionTimer = await this._readSessionTimerFromServer();
            this._startHeartBeat(SESSION_TIMER_HEARTBEAT_INTERVAL_LOW_RATE);
        } catch (err) {
            this.services.logger.error('Failed to read session timer from server on startup', err);
            this._startHeartBeat(SESSION_TIMER_HEARTBEAT_INTERVAL_HIGH_RATE);
        }

        this._startIncrement();

    }

    private _startHeartBeat(interval: TimeSpan): void {
        this._heartBeatIntervalRef = namedSetInterval('SESSION_TIMER_HEART_BEAT_INTERVAL', async () => {
            try {
                this.currentSessionTimer = await this._readSessionTimerFromServer();

                if(interval.totalMilliseconds !== SESSION_TIMER_HEARTBEAT_INTERVAL_LOW_RATE.totalMilliseconds) {
                    clearInterval(this._heartBeatIntervalRef);
                    this._startHeartBeat(SESSION_TIMER_HEARTBEAT_INTERVAL_LOW_RATE);
                }

            } catch (err) {
                this.services.logger.error('Failed update session timer from server', err);
                if(interval.totalMilliseconds !== SESSION_TIMER_HEARTBEAT_INTERVAL_HIGH_RATE.totalMilliseconds) {
                    clearInterval(this._heartBeatIntervalRef);
                    this._startHeartBeat(SESSION_TIMER_HEARTBEAT_INTERVAL_HIGH_RATE);
                }
            }

        }, interval);
    }

    private _startIncrement(): void {
        this._incrementIntervalRef = namedSetInterval('SESSION_TIMER_INCREMENT_INTERVAL', () => {
            this.currentSessionTimer = this.currentSessionTimer.add(SESSION_TIMER_INCREMENT_INTERVAL);
        }, SESSION_TIMER_INCREMENT_INTERVAL.totalMilliseconds);
    }

    private _stopSessionTimer(): void {
        clearInterval(this._incrementIntervalRef);
        clearInterval(this._heartBeatIntervalRef);
        runInAction(() => {
            this.currentSessionTimer = TimeSpan.fromMilliseconds(0);
        });

    }

    private async _readSessionTimerFromServer(): Promise<TimeSpan> {
        const response = await this.services.api.userController.getSessionTimer();

        return TimeSpan.parse(response.SessionTimer);
    }



}